import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import { Field, FilePicker, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import { fetchJSON } from '../utils/api';

// Engagement Status Change — submission form (docs/modules/workflow-esc.md).
// The status is normally set by the workflows themselves (created → first
// submission → QC35 Form approval), so this form exists for the cases they cannot
// cover: marking a case Discontinued, or correcting a
// status that ended up wrong. A reason is mandatory — QM is being asked to
// overwrite formal data. workflow:esc:review holders review; no reviewer pick.

// 目標狀態下拉——New 與 Detail 的 Modify modal 共用，濾現況的規則才不會走散。
// 目前狀態本身不是可選項（後端 validateTarget 也擋，這裡先擋免得白填一輪）；
// 還沒選案（currentStatus 未知）時只留佔位、不列任何狀態——否則灰掉的下拉
// 會把「現況」也列出來。
export const EscStatusSelect = ({
  statuses = [],
  currentStatus = null,
  value,
  onChange,
  disabled = false,
  placeholder = 'Select…',
}) => (
  <select value={value} onChange={onChange} disabled={disabled} className={inputCls}>
    <option value="">{placeholder}</option>
    {currentStatus && statuses
      .filter((s) => s !== currentStatus)
      .map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

const WorkflowEscNew = () => {
  const navigate = useNavigate();
  const [engagements, setEngagements] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [engagementId, setEngagementId] = useState('');
  const [requestedStatus, setRequestedStatus] = useState('');
  const [reason, setReason] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/esc/options');
        setEngagements(data.engagements || []);
        setStatuses(data.statuses || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const selected = useMemo(
    () => engagements.find((e) => String(e.id) === String(engagementId)) || null,
    [engagements, engagementId]
  );

  // Everything still empty, keyed to the control it belongs to — after the
  // first Submit the page marks those controls and lists them in the banner.
  const gaps = useMemo(() => {
    const g = [];
    if (!engagementId) g.push({ key: 'engagementId', label: 'Engagement' });
    if (!requestedStatus) g.push({ key: 'requestedStatus', label: 'New Status' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    return g;
  }, [engagementId, requestedStatus, reason]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    setSaving(true); setError(null);
    try {
      const form = new FormData();
      form.set('engagementId', engagementId);
      form.set('requestedStatus', requestedStatus);
      form.set('reason', reason.trim());
      files.forEach((f) => form.append('files', f));
      const data = await fetchJSON('/api/workflow/esc', { method: 'POST', body: form });
      navigate(`/workflow/esc/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/esc"
      backLabel="Engagement Status Change"
      title="New Engagement Status Change Request"
      subtitle="Request a change to an engagement's status."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={engagementBlockReasons(selected ? [selected] : [], 'esc').length > 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement">
          {/* 分組式單選——與涵蓋選單同一顆元件的 single 模式。 */}
          <GroupedEngagementPicker
            single
            engagements={engagements}
            selectedIds={engagementId ? [Number(engagementId)] : []}
            onChange={(ids) => { setEngagementId(ids[0] ? String(ids[0]) : ''); setRequestedStatus(''); setError(null); }}
          />
          <FieldInvalidInline field="engagementId" text="Required — pick the engagement" />
          <EngagementBlockers selected={selected ? [selected] : []} flow="esc" />
          {selected && (
            <p className="text-xs text-slate-500">Current status: <span className="font-medium text-slate-700">{selected.status}</span></p>
          )}
        </FormSection>

        <FormSection step={2} title="Change the Status To">
          <Field label="New Status" required field="requestedStatus">
            <EscStatusSelect
              statuses={statuses}
              currentStatus={selected ? selected.status : null}
              value={requestedStatus}
              onChange={(e) => setRequestedStatus(e.target.value)}
              disabled={!selected}
              placeholder={selected ? 'Select…' : 'Pick an engagement first'}
            />
          </Field>
        </FormSection>

        <FormSection step={3} title="Reason & Attachments">
          <Field label="Reason" required field="reason">
            <textarea rows="3" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="What happened?" />
          </Field>
          <Field label="Supporting Files">
            <FilePicker multiple kind="docsAndImages"
              onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowEscNew;
